import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserIdentityContext } from '../../../../context/UserIdentityContext';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { InputSwitch } from 'primereact/inputswitch';
import NotificationCardMolecule, { NotificationRecord, NotificationTypeConfig } from '../../NotificationCard/NotificationCardMolecule';
import NotificationSkeletonCardAtom from '../../../atoms/NotificationSkeleton/NotificationSkeletonCardAtom';
import EmptyStateAtom from '../../../atoms/EmptyState/EmptyStateAtom';
import { useToolbarContextOrNull } from '../../../../context/ToolbarContext';
import { NOTIFICATION_TYPE_CONFIGS } from '../../../../config/notificationTypes';
import './NotificationsMode.scss';

export interface NotificationsModeProps {
  /** Active sphere ID (for context - future enhancement for sphere filtering) */
  activeSphereId: string;
  /** Additional sphere IDs (orbitals) - future enhancement for sphere filtering */
  additionalSphereIds?: string[];
  /** Additional CSS classes */
  className?: string;
}

/**
 * NotificationsMode - Full notification feed view
 *
 * Uses shared context data to prevent redundant API calls.
 * Includes filtering, infinite scroll, and mark as read functionality.
 */
const NotificationsMode: React.FC<NotificationsModeProps> = ({
  activeSphereId: _activeSphereId,
  additionalSphereIds: _additionalSphereIds,
  className = '',
}) => {
  const { t } = useTranslation();
  const { identityId: currentUserId } = useUserIdentityContext();
  const observerTarget = useRef<HTMLDivElement>(null);

  // Filter state
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  // Scroll animation - track which cards have animated in
  const [visibleCards, setVisibleCards] = useState<Set<string>>(new Set());
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollObserverRef = useRef<IntersectionObserver | null>(null);

  // Get shared data from context (no duplicate API calls)
  const toolbarContext = useToolbarContextOrNull();

  // No-op function for when context is not available
  const noopAsync = async (): Promise<void> => { /* no-op */ };

  const notifications = toolbarContext?.notificationState.notifications ?? [];
  const isLoading = toolbarContext?.notificationState.isLoading ?? false;
  const error = toolbarContext?.notificationState.error ?? null;
  const unreadCount = toolbarContext?.notificationState.unreadCount ?? 0;
  const hasMore = toolbarContext?.notificationState.hasMore ?? false;
  const markAsRead = toolbarContext?.markNotificationAsRead ?? noopAsync;
  const markAllAsRead = toolbarContext?.markAllNotificationsAsRead ?? noopAsync;
  const fetchMore = toolbarContext?.fetchMoreNotifications ?? noopAsync;

  // Translate notification type configurations
  const NOTIFICATION_TYPES: NotificationTypeConfig[] = useMemo(
    () =>
      NOTIFICATION_TYPE_CONFIGS.map((config) => ({
        value: config.value,
        label: t(config.labelKey),
        icon: config.icon,
      })),
    [t]
  );

  // Filter notifications based on selected filters
  const filteredNotifications = useMemo(() => {
    return notifications.filter((notification) => {
      if (showUnreadOnly && notification.status === 'read') {
        return false;
      }
      if (selectedTypes.length > 0 && !selectedTypes.includes(notification.type)) {
        return false;
      }
      return true;
    });
  }, [notifications, showUnreadOnly, selectedTypes]);

  // Get notification type config
  const getTypeConfig = useCallback(
    (type: string): NotificationTypeConfig | undefined => {
      return NOTIFICATION_TYPES.find((config) => config.value === type);
    },
    [NOTIFICATION_TYPES]
  );


  // Handle notification click
  const handleNotificationClick = useCallback(
    (notification: NotificationRecord) => {
      if (notification.status !== 'read') {
        markAsRead(notification.id);
      }
    },
    [markAsRead]
  );

  // Handle mark all as read
  const handleMarkAllRead = useCallback(async () => {
    if (selectedTypes.length > 0) {
      for (const type of selectedTypes) {
        await markAllAsRead({ type });
      }
    } else {
      await markAllAsRead();
    }
  }, [markAllAsRead, selectedTypes]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          fetchMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, isLoading, fetchMore]);

  // Set up Intersection Observer for scroll animations
  useEffect(() => {
    scrollObserverRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cardId = (entry.target as HTMLElement).dataset.cardId;
            if (cardId) {
              setVisibleCards((prev) => new Set(prev).add(cardId));
              // Unobserve after animating in (animation only happens once)
              scrollObserverRef.current?.unobserve(entry.target);
            }
          }
        });
      },
      {
        root: null, // viewport
        rootMargin: '0px 0px -50px 0px', // trigger slightly before entering viewport
        threshold: 0.1,
      }
    );

    return () => {
      scrollObserverRef.current?.disconnect();
    };
  }, []);

  // Observe cards when they mount
  useEffect(() => {
    const observer = scrollObserverRef.current;
    if (!observer) return;

    cardRefs.current.forEach((element) => {
      observer.observe(element);
    });
  }, [filteredNotifications]);

  // Callback to register card refs
  const setCardRef = useCallback((id: string, element: HTMLDivElement | null) => {
    if (element) {
      cardRefs.current.set(id, element);
      scrollObserverRef.current?.observe(element);
    } else {
      const el = cardRefs.current.get(id);
      if (el) {
        scrollObserverRef.current?.unobserve(el);
        cardRefs.current.delete(id);
      }
    }
  }, []);

  if (!currentUserId) {
    return (
      <div className={`notifications-mode notifications-mode--error ${className}`}>
        <div className="notifications-mode__error">
          <i className="pi pi-exclamation-circle" />
          <span>{t('notifications.userNotAuthenticated', 'User not authenticated')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`notifications-mode ${className}`}>
      {/* Panel title */}
      <div className="notifications-mode__title-bar">
        <i className="pi pi-bell" />
        <span>{t('toolbar.notifications')}</span>
      </div>

      {/* Header with unread count and mark all read */}
      <div className="notifications-mode__header">
        <div className="notifications-mode__header-left">
          <span className="notifications-mode__unread-count">
            {unreadCount > 0 && (
              <>
                <span className="notifications-mode__badge">{unreadCount}</span>
                {t('notifications.unread', 'unread')}
              </>
            )}
          </span>
        </div>
        <Button
          label={t('notifications.markAllRead', 'Mark all read')}
          icon="pi pi-check-double"
          size="small"
          text
          onClick={handleMarkAllRead}
          disabled={unreadCount === 0}
        />
      </div>

      {/* Filter bar */}
      <div className="notifications-mode__filters">
        <div className="notifications-mode__type-filter-wrapper">
          <MultiSelect
            value={selectedTypes}
            options={NOTIFICATION_TYPES}
            onChange={(e) => setSelectedTypes(e.value)}
            optionLabel="label"
            optionValue="value"
            placeholder={t('notifications.filterByType', 'Filter by type')}
            className="notifications-mode__type-filter"
            showClear
            maxSelectedLabels={0}
            selectedItemsLabel={t('notifications.filterByType', 'Filter by type')}
          />
          {selectedTypes.length > 0 && (
            <span className="notifications-mode__filter-badge">{selectedTypes.length}</span>
          )}
        </div>
        <div className="notifications-mode__unread-toggle">
          <InputSwitch
            inputId="show-unread-only"
            checked={showUnreadOnly}
            onChange={(e) => setShowUnreadOnly(e.value)}
          />
          <label htmlFor="show-unread-only">
            {t('notifications.showUnreadOnly', 'Unread only')}
          </label>
        </div>
      </div>

      {/* Notification list */}
      <div className="notifications-mode__content">
        {isLoading && notifications.length === 0 && <NotificationSkeletonCardAtom count={5} />}

        {!isLoading && error && (
          <EmptyStateAtom
            icon="pi-exclamation-triangle"
            title={t('notifications.errorTitle', 'Error loading notifications')}
            description={error}
          />
        )}

        {!isLoading && !error && filteredNotifications.length === 0 && (
          <EmptyStateAtom
            icon="pi-inbox"
            title={showUnreadOnly ? t('notifications.noUnread', 'No unread notifications') : t('notifications.noNotifications', 'No notifications yet')}
            description={showUnreadOnly ? t('notifications.allCaughtUp', 'All caught up!') : t('notifications.whenYouReceive', 'When you receive notifications, they will appear here.')}
          />
        )}

        {!isLoading && !error && filteredNotifications.length > 0 && (
          <>
            <div className="notifications-mode__list">
              {filteredNotifications.map((notification, index) => {
                const isVisible = visibleCards.has(notification.id);
                const animationDelay = `${Math.min(index * 50, 300)}ms`; // Max 300ms delay

                return (
                  <div
                    key={notification.id}
                    ref={(el) => setCardRef(notification.id, el)}
                    data-card-id={notification.id}
                    className={`notifications-mode__card-wrapper ${isVisible ? 'visible' : ''}`}
                    style={{ animationDelay }}
                  >
                    <NotificationCardMolecule
                      notification={notification}
                      typeConfig={getTypeConfig(notification.type)}
                      onClick={handleNotificationClick}
                      onMarkRead={markAsRead}
                    />
                  </div>
                );
              })}
            </div>

            {/* Infinite scroll trigger */}
            {hasMore && (
              <div ref={observerTarget} className="notifications-mode__observer">
                {isLoading && <NotificationSkeletonCardAtom count={3} />}
              </div>
            )}

            {!hasMore && filteredNotifications.length > 10 && (
              <div className="notifications-mode__end">
                <p>{t('notifications.endOfList', "You've reached the end")}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationsMode;
